/**
 * Operator sound preference for the relay desk — one boolean in localStorage,
 * PER OPERATOR.
 *
 * Off by default: an autoplaying chime the operator never asked for is the
 * kind of thing that gets a tab muted, after which the alarm is gone for good.
 * The toggle lives on the settings screen; the stream hook only reads it.
 *
 * BUG-36: the settings card promises «окремо для кожного оператора», but the
 * key had no owner, so one admin's choice followed the next one who signed in
 * on the same browser. The key is now `admin.sound:<operatorId>` (the signed-in
 * admin's `GET /users/me` `_id`). Without an id the setting reads off and is
 * never written. The legacy un-scoped `admin.sound` is ignored, not migrated:
 * with no owner, it is the bug itself.
 */

/** Prefix of the per-operator key; the bare value is the retired legacy key. */
export const SOUND_STORAGE_KEY = 'admin.sound';

/** The signed-in admin's id, or nothing while it is unknown. */
export type OperatorId = string | null | undefined;

export function soundStorageKey(operatorId: string): string {
  return `${SOUND_STORAGE_KEY}:${operatorId}`;
}
/**
 * Served from `apps/admin/public/sounds/` (Vite copies `public/` verbatim). The
 * file is NOT in the repo — see `public/sounds/README.md` for what the owner
 * must drop there. Until it lands, `playNewBidSound` resolves 'missing'.
 */
export const NEW_BID_SOUND_URL = '/sounds/new-bid.mp3';

/** Minimal Storage surface, so the helpers are testable without a DOM. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Some privacy modes throw on access — treat as "no preference stored".
    return null;
  }
}

export function isNewBidSoundEnabled(
  operatorId: OperatorId,
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!operatorId) return false;
  try {
    return store?.getItem(soundStorageKey(operatorId)) === '1';
  } catch {
    return false;
  }
}

export function setNewBidSoundEnabled(
  operatorId: OperatorId,
  enabled: boolean,
  store: KeyValueStore | null = defaultStore(),
): void {
  if (!operatorId) return;
  try {
    store?.setItem(soundStorageKey(operatorId), enabled ? '1' : '0');
  } catch {
    // Quota / privacy-mode failures leave the preference at its previous value.
  }
}

/** Structural view of HTMLAudioElement, injected so tests never touch the DOM. */
export interface Playable {
  play(): Promise<void>;
}

/**
 * What happened when the chime was asked to play.
 * - 'played'   — the browser accepted the play request.
 * - 'missing'  — the source could not be loaded: `NotSupportedError`, which is
 *                how a 404 on the asset (or an unsupported codec) surfaces —
 *                the media element never sees an HTTP status.
 * - 'blocked'  — the autoplay policy refused (`NotAllowedError`).
 * - 'failed'   — any other rejection.
 * - 'no-audio' — the environment has no `Audio` at all (SSR, jsdom, tests).
 */
export type SoundPlayOutcome = 'played' | 'missing' | 'blocked' | 'failed' | 'no-audio';

function classifyPlayError(error: unknown): SoundPlayOutcome {
  const name = error && typeof error === 'object' ? (error as { name?: unknown }).name : undefined;
  if (name === 'NotSupportedError') return 'missing';
  if (name === 'NotAllowedError') return 'blocked';
  return 'failed';
}

/**
 * Play the new-bid chime. Never throws and never rejects — every failure is
 * folded into the resolved {@link SoundPlayOutcome}, so the stream hook can
 * fire-and-forget while the settings Test button reads the answer and tells
 * the operator whether silence means "muted tab" or "file not shipped".
 */
export async function playNewBidSound(
  createAudio: (url: string) => Playable | null = defaultCreateAudio,
): Promise<SoundPlayOutcome> {
  let audio: Playable | null;
  try {
    audio = createAudio(NEW_BID_SOUND_URL);
  } catch {
    return 'no-audio';
  }
  if (!audio) return 'no-audio';

  try {
    // Browsers return a Promise; jsdom's stub returns undefined — awaiting
    // either is fine.
    await audio.play();
    return 'played';
  } catch (error) {
    return classifyPlayError(error);
  }
}

function defaultCreateAudio(url: string): Playable | null {
  return typeof Audio === 'undefined' ? null : new Audio(url);
}
